import Cookies from 'js-cookie';

/** js-cookie ships `export =`; named type imports fail Next.js typecheck. */
type CookieAttributes = NonNullable<Parameters<typeof Cookies.set>[2]>;

/**
 * Canonical Seller Portal cookie names are lowercase `ff_…`. Ingress Lua and the
 * service cookie filter both normalize case, so Secret Menu / Hammer keys are
 * written once in lowercase on `.proxibid.com` — no uppercase twin.
 *
 * This does **not** map `ff_sp_*` catalog names onto `@Feature` cookies
 * (e.g. `ff_sp_new-lot-metadata` stays that name, not `ff_new_lot_metadata`).
 *
 * Cross-subdomain sharing requires a `*.proxibid.com` host (e.g. `localdev.proxibid.com`
 * plus a hosts-file entry). Host-only localhost cookies are not sent to the service.
 */
export const toSellerFeatureFlagCookieName = (name: string): string => name.toLowerCase();

const cookieOptions = (): CookieAttributes => ({
    domain: '.proxibid.com',
    path: '/',
    sameSite: 'Lax',
    secure: process.env.NODE_ENV === 'production',
});

/** Hammer's DevTools Remove only expires the host-only cookie (`path=/`, no domain). */
const HOST_ONLY_REMOVE_OPTIONS = { path: '/' } as const;

const writeCookie = (name: string, value: boolean): void => {
    Cookies.set(name, value ? 'true' : 'false', cookieOptions());
};

const removeCookieEverywhere = (name: string): void => {
    Cookies.remove(name, cookieOptions());
    Cookies.remove(name, HOST_ONLY_REMOVE_OPTIONS);
};

/**
 * Sets or updates a feature flag cookie on the client side.
 * Writes the canonical lowercase name on `.proxibid.com` so wavebid-a2o-service
 * can see it. Local development must use `localdev.proxibid.com` (hosts file)
 * for that domain cookie to be sent.
 */
export const setFeatureFlagCookie = (name: string, value: boolean): void => {
    writeCookie(toSellerFeatureFlagCookieName(name), value);
};

/**
 * Clears a feature flag cookie, its canonical lowercase name, and any leftover
 * uppercase twin. Removes both the `.proxibid.com` replica and Hammer's host-only
 * cookie — js-cookie cannot tell those two apart when the names match, so Remove
 * in Secret Menu must delete both or the override appears stuck.
 */
export const clearFeatureFlagCookie = (name: string): void => {
    removeCookieEverywhere(name);
    const canonical = toSellerFeatureFlagCookieName(name);
    if (canonical !== name) {
        removeCookieEverywhere(canonical);
    }
    const legacyUpper = name.toUpperCase();
    if (legacyUpper !== name && legacyUpper !== canonical) {
        removeCookieEverywhere(legacyUpper);
    }
};

/**
 * Gets a feature flag cookie value on the client side.
 * @param name - The name of the feature flag cookie to retrieve
 * @returns true if the named cookie, its lowercase canonical, or a leftover uppercase twin is `"true"`
 */
export const getFeatureFlagCookie = (name: string): boolean => {
    return (
        Cookies.get(name) === 'true' ||
        Cookies.get(toSellerFeatureFlagCookieName(name)) === 'true' ||
        Cookies.get(name.toUpperCase()) === 'true'
    );
};

/**
 * For each Amplitude / bridge flag key that has a Secret Menu override cookie, write a
 * lowercase canonical cookie on `.proxibid.com`. Leftover uppercase twins are always
 * removed (including when the bridge key is already uppercase and still active).
 * When the source cookie is gone, the domain cookie is removed even if the names already
 * match — otherwise a leftover `.proxibid.com` cookie survives Hammer's host-only Remove.
 * Unrelated cookies are ignored.
 */
export const syncSellerFeatureFlagCookies = (flagKeys: readonly string[] = []): void => {
    const all = Cookies.get();
    if (!all) {
        return;
    }

    flagKeys.forEach((key) => {
        const canonical = toSellerFeatureFlagCookieName(key);
        if (key in all) {
            writeCookie(canonical, all[key] === 'true');
        } else {
            removeCookieEverywhere(canonical);
        }

        const legacyUpper = key.toUpperCase();
        if (legacyUpper !== canonical) {
            removeCookieEverywhere(legacyUpper);
        }
    });
};

const flagKeyFromDevToolsRow = (el: Element): string | undefined =>
    el.closest('tr')?.querySelector('td')?.textContent?.trim() || undefined;

/**
 * Hammer `FeatureFlagDevToolsUI` Remove lives in the flag's table row. Capture-phase
 * listeners use this to clear the domain replica before Hammer re-reads `document.cookie`.
 */
export const featureFlagKeyFromRemoveClick = (event: Event): string | undefined => {
    const target = event.target;
    if (!(target instanceof Element)) {
        return undefined;
    }

    const button = target.closest('button');
    if (button?.textContent?.replace(/\s+/g, ' ').trim() !== 'Remove') {
        return undefined;
    }

    return flagKeyFromDevToolsRow(button);
};

/**
 * Hammer ToggleSwitch is a `role="switch"` button. Capture-phase click sees `aria-checked`
 * *before* Hammer flips it; the next override is the inverse, matching
 * `onToggleChange(!toggled)`.
 */
export const featureFlagToggleFromClick = (event: Event): { enabled: boolean; key: string } | undefined => {
    const target = event.target;
    if (!(target instanceof Element) || featureFlagKeyFromRemoveClick(event)) {
        return undefined;
    }

    const switchEl = target.closest('[role="switch"]');
    if (!(switchEl instanceof HTMLElement)) {
        return undefined;
    }

    const key = flagKeyFromDevToolsRow(switchEl);
    if (!key) {
        return undefined;
    }

    return { enabled: switchEl.getAttribute('aria-checked') !== 'true', key };
};

/**
 * Secret Menu click handler: on Remove, drop host-only + domain cookies and skip the
 * sync write-back (Cookies.get() would still see the domain replica and restore it).
 * On toggle, write the *next* switch value to `.proxibid.com` — Cookies.get() cannot
 * tell Hammer's host-only cookie from a stale domain replica of the same name, which
 * left Cookie Override + On sending `false`.
 */
export const syncSellerCookiesAfterDevToolsClick = (event: Event): void => {
    const removedKey = featureFlagKeyFromRemoveClick(event);
    if (removedKey) {
        clearFeatureFlagCookie(removedKey);
        queueMicrotask(() => {
            clearFeatureFlagCookie(removedKey);
        });
        return;
    }

    const toggle = featureFlagToggleFromClick(event);
    if (!toggle) {
        return;
    }

    setFeatureFlagCookie(toggle.key, toggle.enabled);
    queueMicrotask(() => {
        Cookies.remove(toSellerFeatureFlagCookieName(toggle.key), HOST_ONLY_REMOVE_OPTIONS);
    });
};
